import React, { useRef } from 'react';
import PropTypes from 'prop-types';

// Utils
import { SortDirection } from 'ka-table/enums';
import { useTranslation } from 'react-i18next';
import useModal from '../../../utils/hooks/useModal';
import PermissionsGate, { isGranted } from '../../../utils/gate/permissionsGate';
import { useAppContext } from '../../../store/AppContext';
import SCOPE from '../../../utils/gate/scope';

// Icons
import { ReactComponent as EditIcon } from '../../../assets/icons/edit.svg';

// Components
import SkeletonLoader from '../../molecules/SkeletonLoader/SkeletonLoader';
import GenericTable from '../GenericTable/GenericTable';
import EmptyList from '../../organisms/EmptyList/EmptyList';
import RoleCreationEditionForm from './RoleCreationEditionForm/RoleCreationEditionForm';

function RolesTable({
  roles, setRoles, isFetching, permissions,
}) {
  const { t } = useTranslation();
  const { displayModal } = useModal();
  const [{ user }] = useAppContext();

  const roleEditionForm = useRef();

  const columns = [
    {
      key: 'role', title: t('roles.table.columns.role'), sortDirection: SortDirection.Ascend, bold: true,
    },
    { key: 'actions', title: t('roles.table.columns.actions') },
  ];

  const getRoleRow = (role, index) => ({
    id: index,
    role: role.name,
    actions: (
      !role?.isSuperAdmin ? (
        <PermissionsGate scopes={[SCOPE.update_pla_role, SCOPE.update_ownEst_role]}>
          <button
            type="button"
            onClick={() => displayModal({
              maxWidth: 455,
              title: t('roles.edition.title'),
              content: (
                <RoleCreationEditionForm
                  ref={roleEditionForm}
                  role={role}
                  setRoles={setRoles}
                  permissions={permissions}
                />
              ),
              buttons: [
                {
                  label: t('common.cancel'),
                  theme: 'inverted',
                },
                {
                  label: t('roles.edition.form.submit'),
                  onClick: () => roleEditionForm?.current?.submitForm(),
                  queryName: 'putRole',
                },
              ],
            })}
            className="d-flex a-center c-primary-500 fs-16 font-secondary fw-600 c-pointer"
          >
            <EditIcon className="m-r-10" width={14} height={14} />
            {t('roles.table.actions.edit')}
          </button>
        </PermissionsGate>
      ) : null
    ),
  });

  return (
    (isFetching) ? (
      <>
        <SkeletonLoader height={60} className="m-b-20" />
        <SkeletonLoader height={300} />
      </>
    ) : (
      roles?.length ? (
        <GenericTable
          columns={columns}
          rows={roles.map((role, index) => getRoleRow(role, index))}
        />
      ) : (
        <EmptyList
          imageTheme="users"
          title={t('roles.table.empty.title')}
          text={
            `${t('roles.table.empty.text')} ${isGranted(
              user,
              [SCOPE.create_pla_role, SCOPE.create_ownEst_role],
            )
              ? t('roles.table.empty.add')
              : ''}`
          }
        />
      )
    )
  );
}

RolesTable.propTypes = {
  roles: PropTypes.arrayOf(PropTypes.shape({})),
  setRoles: PropTypes.func,
  isFetching: PropTypes.bool,
  permissions: PropTypes.arrayOf(PropTypes.string),
};

RolesTable.defaultProps = {
  roles: [],
  setRoles: () => {},
  isFetching: false,
  permissions: [],
};

export default RolesTable;
